// PostSuggestedListing.js

import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Modal,
  Alert,
  Platform,
  Image,
  FlatList,
  Dimensions
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const API_BASE = 'https://collabbayadmin.com/APIControllers';

const SCREEN_WIDTH = Dimensions.get('window').width;
const MAX_WIDTH = 720;
const IMAGE_BASE = 'https://collabbayadmin.com/listings/';
const FALLBACK_BANNER =
  'https://images.pexels.com/photos/262978/pexels-photo-262978.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1';

const BANNER_WIDTH = Math.min(SCREEN_WIDTH - 32, MAX_WIDTH);
const BANNER_HEIGHT = SCREEN_WIDTH < 500 ? 190 : 250;

const parseBannerImages = (csv) => {
  if (!csv || !String(csv).trim()) return [FALLBACK_BANNER];

  const arr = String(csv)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((u) => (u.startsWith('http') ? u : IMAGE_BASE + u));

  return arr.length ? arr : [FALLBACK_BANNER];
};

export default function PostSuggestedListing({ navigation }) {
  const bannerListRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [offer, setOffer] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showLaunchOptions, setShowLaunchOptions] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [launchingChoice, setLaunchingChoice] = useState(null);

  const [bannerImages, setBannerImages] = useState([FALLBACK_BANNER]);
  const [bannerIndex, setBannerIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});

  const [form, setForm] = useState({
    ListingID: '',
    BrandName: '',
    Location: '',
    City: '',
    Neighbourhood: '',
    ListingDescription: '',
    ContentBrief: '',
    NonCreditOffer: '',
    Credits: '',
    PreferredAvailability: '',
    OfferCategory: '',
    MinFollowersRequired: '',
    AdditionalBookingDetails: '',
    BrandURL: '',
    LogoImage: '',
    TurnAroundDays: '',
    PauseLiveToggle: '',
    isNudged: ''
  });

  useEffect(() => {
    fetchSuggestedListing();
  }, []);

  useEffect(() => {
    if (bannerImages.length <= 1) return;

    const interval = setInterval(() => {
      setBannerIndex((prev) => {
        const next = (prev + 1) % bannerImages.length;

        bannerListRef.current?.scrollToOffset({
          offset: next * BANNER_WIDTH,
          animated: true
        });

        return next;
      });
    }, 2600);

    return () => clearInterval(interval);
  }, [bannerImages.length]);

  const fetchSuggestedListing = async () => {
    try {
      setLoading(true);

      const endpoint = `${API_BASE}/GetBrandSuggestedListing.php?cacheBust=${Date.now()}`;

      const response = await fetch(endpoint, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/json'
        }
      });

      const rawText = await response.text();
      const data = JSON.parse(rawText);

      if (!data.success || !data.listing) {
        setOffer(null);
        setIsSubscribed(data?.isSubscribed === true);
        setLoading(false);
        return;
      }

      const listing = data.listing;

      setOffer(listing);
      setIsSubscribed(data.isSubscribed === true);

      const images = parseBannerImages(listing.LogoImage || '');
      setBannerImages(images);
      setBannerIndex(0);
      setLoadedImages({});

      setForm({
        ListingID: String(listing.ListingID || ''),
        BrandName: String(listing.BrandName || ''),
        Location: String(listing.Location || ''),
        City: String(listing.City || ''),
        Neighbourhood: String(listing.Neighbourhood || ''),
        ListingDescription: String(listing.ListingDescription || ''),
        ContentBrief: String(listing.ContentBrief || ''),
        NonCreditOffer: String(listing.NonCreditOffer || ''),
        Credits: String(listing.Credits || ''),
        PreferredAvailability: String(listing.PreferredAvailability || ''),
        OfferCategory: String(listing.OfferCategory || ''),
        MinFollowersRequired: String(listing.MinFollowersRequired || ''),
        AdditionalBookingDetails: String(listing.AdditionalBookingDetails || ''),
        BrandURL: String(listing.BrandURL || ''),
        LogoImage: String(listing.LogoImage || ''),
        TurnAroundDays: String(listing.TurnAroundDays || '4'),
        PauseLiveToggle: String(listing.PauseLiveToggle || ''),
        isNudged: String(listing.isNudged || '')
      });
    } catch (error) {
      console.log('Suggested listing fetch error:', error);
      setOffer(null);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const scrollToBanner = (index) => {
    if (!bannerListRef.current || bannerImages.length === 0) return;

    const next = ((index % bannerImages.length) + bannerImages.length) % bannerImages.length;

    bannerListRef.current.scrollToOffset({
      offset: next * BANNER_WIDTH,
      animated: true
    });

    setBannerIndex(next);
  };

  const onBannerScrollEnd = (event) => {
    const x = event?.nativeEvent?.contentOffset?.x || 0;
    const index = Math.round(x / BANNER_WIDTH);

    if (!Number.isNaN(index)) {
      setBannerIndex(index);
    }
  };

  const validateForm = () => {
    if (!form.NonCreditOffer.trim()) {
      Alert.alert('Missing gifted offer', 'Please add what you are happy to gift creators.');
      return false;
    }

    if (!form.Credits.trim()) {
      Alert.alert('Missing menu value', 'Please add the rough menu-value of this gifted offer.');
      return false;
    }

    if (!termsAccepted) {
      Alert.alert('Terms required', 'Please confirm you accept the collabbay™ terms before posting.');
      return false;
    }

    return true;
  };

  const postSuggestedListing = async () => {
    if (posting) return;
    if (!validateForm()) return;

    setPosting(true);

    try {
      const response = await fetch(`${API_BASE}/BrandPostSuggestedListing.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(form)
      });

      const rawText = await response.text();
      let result = null;

      try {
        result = JSON.parse(rawText);
      } catch (e) {
        Alert.alert('Server Error', 'The server did not return valid JSON.');
        setPosting(false);
        return;
      }

      if (!response.ok || !result?.success) {
        Alert.alert('Error', result?.error || 'Could not post your suggested offer.');
        setPosting(false);
        return;
      }

      setShowLaunchOptions(true);
    } catch (error) {
      Alert.alert('Network Error', 'Please check your connection and try again.');
    } finally {
      setPosting(false);
    }
  };

  const finishOfferLaunch = (choice) => {
    if (launchingChoice) return;

    setLaunchingChoice(choice);

    setShowLaunchOptions(false);

    if (choice === 'standard') {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.location.href = 'https://collabbayadmin.com/subscribe';
        return;
      }

      navigation.navigate('PaymentPage');
      return;
    }

    navigation.navigate('MyOffers', { forceRefresh: true });
    setLaunchingChoice(null);
  };

  if (loading) {
    return (
      <View style={styles.loadingWrapper}>
        <ActivityIndicator size="large" color="#111" />
        <Text style={styles.loadingTitle}>Loading your suggested offer</Text>
        <Text style={styles.loadingSub}>Getting your prepared listing ready...</Text>
      </View>
    );
  }

  if (!offer) {
    return (
      <View style={styles.emptyWrapper}>
        <View style={styles.emptyCard}>
          <View style={styles.emptyIcon}>
            <Ionicons name="document-text-outline" size={28} color="#111" />
          </View>

          <Text style={styles.emptyTitle}>No suggested offer found</Text>

          <Text style={styles.emptyText}>
            If collabbay™ prepares a suggested listing for your account, it will appear here.
          </Text>

          <TouchableOpacity style={styles.primaryButton} onPress={fetchSuggestedListing}>
            <Text style={styles.primaryButtonText}>Refresh</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={() => navigation.navigate('BusinessHomepage')}
          >
            <Text style={styles.secondaryButtonText}>Back to dashboard</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.page} showsVerticalScrollIndicator={false}>
        <View style={styles.topRow}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Ionicons name="chevron-back" size={20} color="#111" />
          </TouchableOpacity>

          <View style={styles.topPill}>
            <Ionicons name="bulb-outline" size={14} color="#111" />
            <Text style={styles.topPillText}>Suggested for you</Text>
          </View>
        </View>

        <View style={styles.pageHeader}>
          <Text style={styles.title}>Your suggested offer is ready.</Text>
          <Text style={styles.headerSubText}>
            Suggested by our agents using collabbay™ data.
          </Text>
        </View>

        <View style={styles.mediaJoinedCard}>
          <View style={styles.bannerCard}>
            <FlatList
              ref={bannerListRef}
              data={bannerImages}
              horizontal
              pagingEnabled
              showsHorizontalScrollIndicator={false}
              keyExtractor={(item, index) => item + index}
              getItemLayout={(_, index) => ({
                length: BANNER_WIDTH,
                offset: BANNER_WIDTH * index,
                index
              })}
              onMomentumScrollEnd={onBannerScrollEnd}
              style={styles.bannerList}
              renderItem={({ item, index }) => (
                <View style={styles.bannerImageWrap}>
                  {!loadedImages[index] && <View style={styles.imageSkeleton} />}

                  <Image
                    source={{ uri: item }}
                    style={[
                      styles.bannerImage,
                      { display: loadedImages[index] ? 'flex' : 'none' }
                    ]}
                    resizeMode="cover"
                    onLoad={() =>
                      setLoadedImages((prev) => ({
                        ...prev,
                        [index]: true
                      }))
                    }
                    onError={() =>
                      setLoadedImages((prev) => ({
                        ...prev,
                        [index]: true
                      }))
                    }
                  />
                </View>
              )}
            />

            {bannerImages.length > 1 && (
              <>
                <TouchableOpacity
                  style={styles.bannerArrow}
                  onPress={() => scrollToBanner(bannerIndex + 1)}
                >
                  <Ionicons name="chevron-forward" size={19} color="#111" />
                </TouchableOpacity>

                <View style={styles.dotsWrap}>
                  {bannerImages.map((_, index) => (
                    <TouchableOpacity key={index} onPress={() => scrollToBanner(index)}>
                      <View style={[styles.dot, index === bannerIndex && styles.dotActive]} />
                    </TouchableOpacity>
                  ))}
                </View>
              </>
            )}
          </View>

          <View style={styles.descriptionJoined}>
            <Text style={styles.descriptionText}>
              {form.ListingDescription || 'No description added yet.'}
            </Text>
          </View>
        </View>

        <View style={styles.infoCard}>
          <View style={styles.infoIconCircle}>
            <Ionicons name="information-circle-outline" size={22} color="#1f5d8f" />
          </View>

          <View style={{ flex: 1 }}>
            <Text style={styles.infoTitle}>Built from what tends to work.</Text>
            <Text style={styles.infoText}>
              We’ve used your venue details, the type of offer creators usually respond to, and marketplace patterns from collabbay™ to prepare a strong starting point. You can amend the gifted offer, rough value and content brief below before posting. Once it’s live, creators can request the offer and you still decide who to accept.
            </Text>
          </View>
        </View>

        <View style={styles.summaryCard}>
          <View style={styles.cardHeaderRow}>
            <Text style={styles.cardTitle}>Suggested offer</Text>
            <View style={styles.softBadge}>
              <Text style={styles.softBadgeText}>Editable below</Text>
            </View>
          </View>

          <View style={styles.offerBox}>
            <View style={styles.offerIcon}>
              <Ionicons name="gift-outline" size={20} color="#111" />
            </View>

            <View style={{ flex: 1 }}>
              <Text style={styles.offerLabel}>Gifted offer</Text>
              <Text style={styles.offerValue}>{form.NonCreditOffer || 'Not set'}</Text>
            </View>
          </View>

          <View style={styles.valueRow}>
            <View style={styles.valueTile}>
              <Ionicons name="wallet-outline" size={19} color="#111" />
              <Text style={styles.valueLabel}>Rough menu-value</Text>
              <Text style={styles.valueNumber}>£{form.Credits || '0'}</Text>
            </View>

            <View style={styles.valueTile}>
              <Ionicons name="camera-outline" size={19} color="#111" />
              <Text style={styles.valueLabel}>Creator content</Text>
              <Text style={styles.valueNumberSmall}>1 reel</Text>
            </View>
          </View>
        </View>

        <View style={styles.formCard}>
          <View style={styles.sectionHeaderBlock}>
            <View style={styles.sectionHeaderRow}>
              <Ionicons name="create-outline" size={18} color="#111" />
              <Text style={styles.cardTitleSmall}>Amend these details</Text>
            </View>

            <Text style={styles.helperText}>
              The fields in this soft highlighted section can be changed before posting. Everything else is just a preview of the suggested listing.
            </Text>
          </View>

          <View style={styles.editablePanel}>
            <View style={styles.fieldGroup}>
              <Text style={styles.label}>What are you happy to gift to get influencers in?</Text>
              <TextInput
                value={form.NonCreditOffer}
                onChangeText={(value) => updateField('NonCreditOffer', value)}
                style={styles.input}
                placeholder="E.g. 2 mains, 2 drinks"
                placeholderTextColor="#888"
                multiline
              />
            </View>

            <View style={styles.fieldGroup}>
              <Text style={styles.label}>What is the rough menu-value of this gifted offer?</Text>
              <TextInput
                value={form.Credits}
                onChangeText={(value) => updateField('Credits', value.replace(/[^0-9]/g, ''))}
                style={styles.input}
                placeholder="E.g. 70"
                placeholderTextColor="#888"
                keyboardType="numeric"
              />
            </View>

            <View style={styles.fieldGroup}>
              <Text style={styles.label}>Content brief for 1 reel</Text>
              <TextInput
                value={form.ContentBrief}
                onChangeText={(value) => updateField('ContentBrief', value)}
                style={[styles.input, styles.textArea]}
                placeholder="What should the creator include in their reel?"
                placeholderTextColor="#888"
                multiline
              />
            </View>

            <View style={styles.fieldGroup}>
              <Text style={styles.label}>Location</Text>
              <TextInput
                value={form.Location}
                onChangeText={(value) => updateField('Location', value)}
                style={styles.input}
                placeholder="Venue address"
                placeholderTextColor="#888"
              />
            </View>
          </View>

          <View style={styles.recapCard}>
            <View style={styles.recapTopRow}>
              <View style={styles.recapIcon}>
                <Ionicons name="checkmark-circle-outline" size={21} color="#111" />
              </View>
              <Text style={styles.recapTitle}>Quick recap</Text>
            </View>

            <Text style={styles.recapText}>
              Post this offer, accept creators you like, and only when you accept a creator will you provide:
            </Text>

            <View style={styles.recapBullet}>
              <Text style={styles.recapBulletDot}>•</Text>
              <Text style={styles.recapBulletText}>{form.NonCreditOffer || 'your listed offer'} in exchange for 1 reel.</Text>
            </View>

            <View style={styles.recapBullet}>
              <Text style={styles.recapBulletDot}>•</Text>
              <Text style={styles.recapBulletText}>The creator’s content is due in {form.TurnAroundDays || '4'} days.</Text>
            </View>

            <View style={styles.recapBullet}>
              <Text style={styles.recapBulletDot}>•</Text>
              <Text style={styles.recapBulletText}>You stay in control — creator requests still need your approval.</Text>
            </View>
          </View>

          <TouchableOpacity
            activeOpacity={0.86}
            style={styles.checkboxRow}
            onPress={() => setTermsAccepted(!termsAccepted)}
          >
            <View style={[styles.checkbox, termsAccepted && styles.checkboxActive]}>
              {termsAccepted && <Ionicons name="checkmark" size={15} color="#111" />}
            </View>

            <Text style={styles.checkboxText}>
              I accept the collabbay™ terms of use and consent to this offer being shown publicly in the marketplace.
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            activeOpacity={0.9}
            style={[styles.postButton, posting && { opacity: 0.65 }]}
            onPress={postSuggestedListing}
            disabled={posting}
          >
            {posting ? (
              <ActivityIndicator color="#111" />
            ) : (
              <>
                <Text style={styles.postButtonText}>Looks good, post my offer</Text>
                <Ionicons name="arrow-forward" size={18} color="#111" />
              </>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>

      <Modal transparent animationType="fade" visible={showLaunchOptions}>
        <View style={styles.successOverlay}>
          <View style={styles.successModalCard}>
            <ScrollView
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.successModalScroll}
            >
              <View style={styles.successIconWrap}>
                <Ionicons name="checkmark-circle" size={70} color="#1f8f3a" />
              </View>

              <Text style={styles.niceWorkText}>Nice work.</Text>
              <Text style={styles.successTitle}>Choose your launch option</Text>

              <Text style={styles.successSubtitle}>
                Your offer has been prepared. Choose how you want it positioned next.
              </Text>

              <View style={styles.launchBlock}>
                <Text style={styles.launchTitle}>Launch option</Text>

                <View style={styles.launchOptionsWrap}>
                  {!isSubscribed ? (
                    <TouchableOpacity
                      activeOpacity={0.88}
                      style={[
                        styles.launchOptionCard,
                        styles.standardOptionCard,
                        launchingChoice !== null && styles.disabledCard
                      ]}
                      disabled={launchingChoice !== null}
                      onPress={() => finishOfferLaunch('standard')}
                    >
                      <View style={styles.optionTopRow}>
                        <View>
                          <Text style={styles.planName}>Standard</Text>
                          <Text style={styles.planMiniText}>Recommended</Text>
                        </View>

                        <View style={styles.freeBadge}>
                          <View style={styles.priceRow}>
                            <Text style={styles.freePrice}>£0</Text>
                            <Text style={styles.oldPrice}>£70</Text>
                          </View>
                        </View>
                      </View>

                      <View style={styles.launchFeatureRow}>
                        <Ionicons name="people-outline" size={17} color="#111" style={styles.featureIcon} />
                        <Text style={styles.featureText}>Show my offer to premium creators</Text>
                      </View>

                      <View style={styles.launchFeatureRow}>
                        <Ionicons name="trending-up-outline" size={17} color="#111" style={styles.featureIcon} />
                        <Text style={styles.featureText}>Rank my offer as priority amongst 200+ others</Text>
                      </View>

                      <View style={styles.launchFeatureRow}>
                        <Ionicons name="paper-plane-outline" size={17} color="#111" style={styles.featureIcon} />
                        <Text style={styles.featureText}>Allow me to invite creators to this offer</Text>
                      </View>

                      <Text style={styles.noChargeText}>No charge today. Cancel anytime.</Text>

                      <View style={styles.standardButton}>
                        {launchingChoice === 'standard' ? (
                          <ActivityIndicator color="#111" />
                        ) : (
                          <Text style={styles.standardButtonText}>Launch as Standard</Text>
                        )}
                      </View>
                    </TouchableOpacity>
                  ) : (
                    <TouchableOpacity
                      activeOpacity={0.88}
                      style={[
                        styles.launchOptionCard,
                        styles.standardOptionCard,
                        launchingChoice !== null && styles.disabledCard
                      ]}
                      disabled={launchingChoice !== null}
                      onPress={() => finishOfferLaunch('restricted')}
                    >
                      <View style={styles.optionTopRow}>
                        <View>
                          <Text style={styles.planName}>Standard</Text>
                          <Text style={styles.planMiniText}>Active</Text>
                        </View>

                        <View style={styles.activeBadge}>
                          <Text style={styles.activeBadgeText}>ACTIVE</Text>
                        </View>
                      </View>

                      <View style={styles.launchFeatureRow}>
                        <Ionicons name="sparkles-outline" size={17} color="#111" style={styles.featureIcon} />
                        <Text style={styles.featureText}>Premium creator visibility available</Text>
                      </View>

                      <View style={styles.launchFeatureRow}>
                        <Ionicons name="trending-up-outline" size={17} color="#111" style={styles.featureIcon} />
                        <Text style={styles.featureText}>Priority tools available</Text>
                      </View>

                      <View style={styles.standardButton}>
                        <Text style={styles.standardButtonText}>Go to My Offers</Text>
                      </View>
                    </TouchableOpacity>
                  )}

                  <TouchableOpacity
                    activeOpacity={0.88}
                    style={[
                      styles.launchOptionCard,
                      styles.restrictedOptionCard,
                      launchingChoice !== null && styles.disabledCard
                    ]}
                    disabled={launchingChoice !== null}
                    onPress={() => finishOfferLaunch('restricted')}
                  >
                    <View style={styles.optionTopRow}>
                      <View>
                        <Text style={styles.planName}>Restricted</Text>
                        <Text style={styles.planMiniText}>Basic access</Text>
                      </View>
                    </View>

                    <View style={styles.launchFeatureRow}>
                      <Ionicons name="checkmark-outline" size={17} color="#333" style={styles.featureIcon} />
                      <Text style={styles.restrictedFeatureText}>Offer stays live</Text>
                    </View>

                    <View style={styles.launchFeatureRow}>
                      <Ionicons name="checkmark-outline" size={17} color="#333" style={styles.featureIcon} />
                      <Text style={styles.restrictedFeatureText}>Creators can request it</Text>
                    </View>

                    <View style={styles.launchFeatureRow}>
                      <Ionicons name="remove-outline" size={17} color="#777" style={styles.featureIcon} />
                      <Text style={styles.mutedFeatureText}>Restricted visibility and access limits</Text>
                    </View>

                    <View style={styles.restrictedButton}>
                      {launchingChoice === 'restricted' ? (
                        <ActivityIndicator color="#111" />
                      ) : (
                        <Text style={styles.restrictedButtonText}>Launch as Restricted</Text>
                      )}
                    </View>
                  </TouchableOpacity>
                </View>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#f3f3f3' },
  page: { width: '100%', maxWidth: MAX_WIDTH, alignSelf: 'center', padding: 16, paddingBottom: 56 },

  loadingWrapper: { flex: 1, backgroundColor: '#f3f3f3', alignItems: 'center', justifyContent: 'center', padding: 24 },
  loadingTitle: { marginTop: 14, fontSize: 16, fontWeight: '700', color: '#111' },
  loadingSub: { marginTop: 4, fontSize: 13, color: '#666', fontWeight: '400' },

  emptyWrapper: { flex: 1, backgroundColor: '#f3f3f3', alignItems: 'center', justifyContent: 'center', padding: 18 },
  emptyCard: { backgroundColor: '#fff', borderRadius: 28, padding: 24, alignItems: 'center', width: '100%', maxWidth: 440, borderWidth: 1, borderColor: '#e9e9e9' },
  emptyIcon: { width: 58, height: 58, borderRadius: 29, backgroundColor: '#fdf05d', alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  emptyTitle: { fontSize: 20, fontWeight: '800', color: '#111', textAlign: 'center' },
  emptyText: { fontSize: 13, color: '#666', textAlign: 'center', lineHeight: 19, marginTop: 8 },
  primaryButton: { marginTop: 18, backgroundColor: '#fdf05d', borderRadius: 14, paddingVertical: 13, paddingHorizontal: 22, borderWidth: 1, borderColor: '#e8dd00' },
  primaryButtonText: { fontWeight: '800', color: '#111' },
  secondaryButton: { marginTop: 10, backgroundColor: '#f3f3f3', borderRadius: 14, paddingVertical: 13, paddingHorizontal: 22 },
  secondaryButtonText: { fontWeight: '700', color: '#111' },

  topRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  backButton: { width: 38, height: 38, borderRadius: 19, backgroundColor: '#fff', alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: '#e8e8e8' },
  topPill: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 999, paddingVertical: 9, paddingHorizontal: 13, borderWidth: 1, borderColor: '#e8e8e8' },
  topPillText: { marginLeft: 6, fontSize: 12, fontWeight: '700', color: '#111' },

  pageHeader: { backgroundColor: '#fff', borderRadius: 24, padding: 20, marginBottom: 14, borderWidth: 1, borderColor: '#e9e9e9' },
  title: { fontSize: 28, fontWeight: '800', color: '#111', lineHeight: 34, marginBottom: 6 },
  headerSubText: { fontSize: 14, color: '#555', lineHeight: 20, fontWeight: '400' },

  mediaJoinedCard: { width: BANNER_WIDTH, alignSelf: 'center', backgroundColor: '#fff', borderRadius: 26, overflow: 'hidden', borderWidth: 1, borderColor: '#e9e9e9', marginBottom: 14 },
  bannerCard: { width: BANNER_WIDTH, height: BANNER_HEIGHT, backgroundColor: '#e9e9e9' },
  bannerList: { width: BANNER_WIDTH, height: BANNER_HEIGHT },
  bannerImageWrap: { width: BANNER_WIDTH, height: BANNER_HEIGHT, backgroundColor: '#e9e9e9' },
  bannerImage: { width: BANNER_WIDTH, height: BANNER_HEIGHT },
  imageSkeleton: { position: 'absolute', width: BANNER_WIDTH, height: BANNER_HEIGHT, backgroundColor: '#e1e1e1' },
  bannerArrow: { position: 'absolute', right: 12, top: '50%', transform: [{ translateY: -18 }], width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.95)', alignItems: 'center', justifyContent: 'center' },
  dotsWrap: { position: 'absolute', bottom: 10, left: 0, right: 0, flexDirection: 'row', justifyContent: 'center' },
  dot: { width: 6, height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.7)', marginHorizontal: 3 },
  dotActive: { width: 16, backgroundColor: '#fdf05d' },
  descriptionJoined: { padding: 16, backgroundColor: '#fff' },
  descriptionText: { fontSize: 14, color: '#555', lineHeight: 21, fontWeight: '400' },

  infoCard: { backgroundColor: '#eef7ff', borderColor: '#cfe7ff', borderWidth: 1, borderRadius: 22, padding: 15, marginBottom: 14, flexDirection: 'row', alignItems: 'flex-start' },
  infoIconCircle: { width: 38, height: 38, borderRadius: 19, backgroundColor: '#dff0ff', alignItems: 'center', justifyContent: 'center', marginRight: 11 },
  infoTitle: { fontSize: 14, fontWeight: '700', color: '#12334f', marginBottom: 5 },
  infoText: { fontSize: 13, color: '#29445c', lineHeight: 19, fontWeight: '400' },

  summaryCard: { backgroundColor: '#fff', borderRadius: 24, padding: 18, marginBottom: 14, borderWidth: 1, borderColor: '#e9e9e9' },
  cardHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  cardTitle: { fontSize: 19, fontWeight: '800', color: '#111' },
  cardTitleSmall: { fontSize: 17, fontWeight: '700', color: '#111', marginLeft: 7 },
  softBadge: { backgroundColor: '#f3f3f3', paddingVertical: 7, paddingHorizontal: 11, borderRadius: 999 },
  softBadgeText: { fontSize: 11, fontWeight: '700', color: '#555' },
  offerBox: { backgroundColor: '#fdf05d', borderRadius: 18, padding: 14, flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12 },
  offerIcon: { width: 38, height: 38, borderRadius: 19, backgroundColor: 'rgba(255,255,255,0.75)', alignItems: 'center', justifyContent: 'center', marginRight: 11 },
  offerLabel: { fontSize: 12, color: '#333', fontWeight: '700', marginBottom: 4 },
  offerValue: { fontSize: 16, color: '#111', fontWeight: '600', lineHeight: 22 },
  valueRow: { flexDirection: SCREEN_WIDTH < 520 ? 'column' : 'row', gap: 10 },
  valueTile: { flex: 1, backgroundColor: '#f3f3f3', borderRadius: 18, padding: 14, borderWidth: 1, borderColor: '#ececec' },
  valueLabel: { marginTop: 8, fontSize: 12, color: '#666', fontWeight: '600' },
  valueNumber: { marginTop: 3, fontSize: 22, fontWeight: '800', color: '#111' },
  valueNumberSmall: { marginTop: 5, fontSize: 15, fontWeight: '700', color: '#111' },

  formCard: { backgroundColor: '#fff', borderRadius: 24, padding: 18, borderWidth: 1, borderColor: '#e9e9e9' },
  sectionHeaderBlock: { backgroundColor: '#fafafa', borderRadius: 18, padding: 14, borderWidth: 1, borderColor: '#eeeeee', marginBottom: 14 },
  sectionHeaderRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  helperText: { fontSize: 13, color: '#666', lineHeight: 18, marginTop: 4, fontWeight: '400' },
  editablePanel: { backgroundColor: '#fffef0', borderRadius: 22, borderWidth: 1, borderColor: '#f3eeb6', padding: 12, marginBottom: 14 },
  fieldGroup: { marginBottom: 10 },
  label: { fontSize: 13, fontWeight: '600', color: '#222', marginBottom: 7 },
  input: { backgroundColor: '#fff', borderRadius: 15, borderWidth: 1, borderColor: '#dedede', padding: 14, fontSize: 14, color: '#111', minHeight: 48, fontWeight: '400' },
  textArea: { minHeight: 92, textAlignVertical: 'top' },

  recapCard: { backgroundColor: '#fafafa', borderRadius: 20, padding: 14, borderWidth: 1, borderColor: '#eeeeee', marginTop: 2, marginBottom: 14 },
  recapTopRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  recapIcon: { width: 34, height: 34, borderRadius: 17, backgroundColor: '#fdf05d', alignItems: 'center', justifyContent: 'center', marginRight: 9 },
  recapTitle: { fontSize: 15, color: '#111', fontWeight: '700', flex: 1 },
  recapText: { fontSize: 13, color: '#555', lineHeight: 19, fontWeight: '400', marginBottom: 8 },
  recapBullet: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 5 },
  recapBulletDot: { fontSize: 15, fontWeight: '800', color: '#111', marginRight: 7, lineHeight: 19 },
  recapBulletText: { flex: 1, fontSize: 13, color: '#333', lineHeight: 19, fontWeight: '500' },

  checkboxRow: { flexDirection: 'row', alignItems: 'flex-start', backgroundColor: '#f8f8f8', borderRadius: 18, padding: 14, borderWidth: 1, borderColor: '#ececec', marginTop: 2, marginBottom: 15 },
  checkbox: { width: 22, height: 22, borderRadius: 8, borderWidth: 1.5, borderColor: '#111', marginRight: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff' },
  checkboxActive: { backgroundColor: '#fdf05d' },
  checkboxText: { flex: 1, fontSize: 12, color: '#444', lineHeight: 18, fontWeight: '400' },
  postButton: { backgroundColor: '#fdf05d', borderRadius: 18, paddingVertical: 16, paddingHorizontal: 18, borderWidth: 1, borderColor: '#e8dd00', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' },
  postButtonText: { fontSize: 15, fontWeight: '700', color: '#111', marginRight: 7 },

  successOverlay: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.62)', padding: 14 },
  successModalCard: { backgroundColor: 'white', borderRadius: 24, width: '94%', maxWidth: 640, maxHeight: '92%', borderWidth: 1, borderColor: '#e8e8e8' },
  successModalScroll: { padding: 20 },
  successIconWrap: { alignItems: 'center', marginBottom: 4 },
  niceWorkText: { fontSize: 13, fontWeight: '800', color: '#1f8f3a', textAlign: 'center', marginBottom: 4 },
  successTitle: { fontSize: 22, fontWeight: '800', textAlign: 'center', color: '#111', marginBottom: 6 },
  successSubtitle: { fontSize: 13, color: '#444', textAlign: 'center', lineHeight: 18, marginBottom: 16 },

  launchBlock: { backgroundColor: '#fff', borderRadius: 18, padding: 0 },
  launchTitle: { fontSize: 17, fontWeight: '800', color: '#111', marginBottom: 10 },
  launchOptionsWrap: { flexDirection: 'column' },
  launchOptionCard: { width: '100%', borderRadius: 18, padding: 15, borderWidth: 1, marginBottom: 10 },
  standardOptionCard: { backgroundColor: '#effff3', borderColor: '#28a745' },
  restrictedOptionCard: { backgroundColor: '#fff', borderColor: '#e5e5e5' },
  disabledCard: { opacity: 0.65 },
  optionTopRow: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 },
  planName: { fontSize: 19, fontWeight: '900', color: '#111' },
  planMiniText: { fontSize: 12, color: '#555', fontWeight: '700', marginTop: 2 },
  freeBadge: { backgroundColor: '#111', borderRadius: 14, paddingHorizontal: 11, paddingVertical: 8, alignItems: 'center', minWidth: 86 },
  priceRow: { flexDirection: 'row', alignItems: 'center' },
  freePrice: { color: '#fdf05d', fontWeight: '900', fontSize: 16, marginRight: 7 },
  oldPrice: { color: '#fff', fontWeight: '700', fontSize: 12, textDecorationLine: 'line-through', opacity: 0.75 },
  activeBadge: { backgroundColor: '#111', borderRadius: 14, paddingHorizontal: 11, paddingVertical: 8 },
  activeBadgeText: { color: '#fdf05d', fontSize: 11, fontWeight: '900' },
  launchFeatureRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 9 },
  featureIcon: { marginRight: 8, marginTop: 1 },
  featureText: { fontSize: 13, color: '#111', lineHeight: 18, flex: 1, fontWeight: '700' },
  restrictedFeatureText: { fontSize: 13, color: '#333', lineHeight: 18, flex: 1, fontWeight: '600' },
  mutedFeatureText: { fontSize: 13, color: '#777', lineHeight: 18, flex: 1, fontWeight: '500' },
  noChargeText: { fontSize: 12, color: '#2f6f3e', fontWeight: '800', marginTop: 2, marginBottom: 12 },
  standardButton: { backgroundColor: '#fdf05d', borderRadius: 12, paddingVertical: 13, marginTop: 6, borderWidth: 1, borderColor: '#111' },
  standardButtonText: { textAlign: 'center', fontWeight: '900', color: '#111' },
  restrictedButton: { backgroundColor: '#f3f3f3', borderRadius: 12, paddingVertical: 13, marginTop: 6, borderWidth: 1, borderColor: '#e2e2e2' },
  restrictedButtonText: { textAlign: 'center', fontWeight: '800', color: '#111' }
});